import React, { Fragment, useState } from 'react';
import { Breadcrumbs } from '../../../AbstractElements';
import { Container, Row, Col, Card, CardHeader, CardBody, Form, FormGroup, Label, Input, Button } from 'reactstrap';
import { UserPlus } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import API_BASE_URL from '../../../Config/api';
import authHeader from '../../Customer/authHeader';

const AddCustomer = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        store_name: '',
        extra_info: '',
        vat_eori: '',
        email: '',
        tel: '',
        shipping_address: '',
        billing_address: '',
        country: '',
        showroom: ''
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
        // Clear error for this field
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.store_name.trim()) {
            newErrors.store_name = 'Store name is required';
        }

        if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }

        if (formData.tel && !/^\+?[\d\s-()]+$/.test(formData.tel)) {
            newErrors.tel = 'Invalid phone number format';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);

        try {
            const response = await fetch(`${API_BASE_URL}/api/customers`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeader().headers,
                },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                const data = await response.json();
                if (data.login && data.login.tempPassword) {
                    // The password is shown either way: when the welcome email failed
                    // it's the only copy, and when it went out the admin may still be
                    // asked for it over the phone.
                    const delivery = data.emailSent
                        ? `<span style="color:#2e7d32">A welcome email with these details has been sent to the customer.</span>`
                        : `<span style="color:#b45309">The welcome email could not be sent${data.emailError ? ` (${data.emailError})` : ''}, so please pass these on yourself.</span>`;
                    await Swal.fire({
                        title: 'Customer added',
                        html: `<div style="text-align:left">A login was created.<br/><br/>
                            <strong>Email:</strong> ${data.login.email}<br/>
                            <strong>Temp password:</strong> <code style="font-size:16px">${data.login.tempPassword}</code><br/><br/>
                            ${delivery}<br/><br/>
                            <small>This password is shown only once.</small></div>`,
                        icon: 'success',
                    });
                } else {
                    await Swal.fire({
                        title: 'Customer added',
                        text: data.loginNote || 'No login was created (no usable email). You can generate one later from the customer list.',
                        icon: 'success',
                    });
                }
                navigate(`${process.env.PUBLIC_URL}/all-customers`);
            } else {
                const errorData = await response.json();
                alert(`Failed to add customer: ${errorData.message || errorData.error || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error adding customer:', error);
            alert('Error adding customer. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate(`${process.env.PUBLIC_URL}/all-customers`);
    };

    return (
        <Fragment>
            <Breadcrumbs parent="Customers" title="Add Customer" mainTitle="Add Customer" />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader>
                                <h5>
                                    <UserPlus className="me-2" size={20} />
                                    Add New Customer
                                </h5>
                            </CardHeader>
                            <CardBody>
                                <Form onSubmit={handleSubmit}>
                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="store_name">Store Name <span className="text-danger">*</span></Label>
                                                <Input
                                                    type="text"
                                                    name="store_name"
                                                    id="store_name"
                                                    value={formData.store_name}
                                                    onChange={handleChange}
                                                    invalid={!!errors.store_name}
                                                    placeholder="Enter store name"
                                                />
                                                {errors.store_name && <div className="text-danger small">{errors.store_name}</div>}
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="vat_eori">VAT/EORI</Label>
                                                <Input
                                                    type="text"
                                                    name="vat_eori"
                                                    id="vat_eori"
                                                    value={formData.vat_eori}
                                                    onChange={handleChange}
                                                    placeholder="Enter VAT/EORI number"
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md="12">
                                            <FormGroup>
                                                <Label for="extra_info">Extra Information</Label>
                                                <Input
                                                    type="textarea"
                                                    name="extra_info"
                                                    rows="4"
                                                    value={formData.extra_info}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, extra_info: e.target.value }))}
                                                    placeholder="Enter extra information about this customer"
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="email">Email</Label>
                                                <Input
                                                    type="email"
                                                    name="email"
                                                    id="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    invalid={!!errors.email}
                                                    placeholder="Enter email address"
                                                />
                                                {errors.email && <div className="text-danger small">{errors.email}</div>}
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="tel">Tel</Label>
                                                <Input
                                                    type="text"
                                                    name="tel"
                                                    id="tel"
                                                    value={formData.tel}
                                                    onChange={handleChange}
                                                    invalid={!!errors.tel}
                                                    placeholder="Enter phone number"
                                                />
                                                {errors.tel && <div className="text-danger small">{errors.tel}</div>}
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="shipping_address">Shipping Address</Label>
                                                <Input
                                                    type="textarea"
                                                    name="shipping_address"
                                                    id="shipping_address"
                                                    value={formData.shipping_address}
                                                    onChange={handleChange}
                                                    placeholder="Enter shipping address"
                                                    rows="3"
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="billing_address">Billing Address</Label>
                                                <Input
                                                    type="textarea"
                                                    name="billing_address"
                                                    id="billing_address"
                                                    value={formData.billing_address}
                                                    onChange={handleChange}
                                                    placeholder="Enter billing address"
                                                    rows="3"
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md="12">
                                            <FormGroup>
                                                <Label for="showroom">Showroom</Label>
                                                <Input
                                                    type="text"
                                                    name="showroom"
                                                    id="showroom"
                                                    value={formData.showroom}
                                                    onChange={handleChange}
                                                    placeholder="Enter showroom location"
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <div className="mt-3">
                                        <Button color="primary" type="submit" disabled={submitting}>
                                            {submitting ? 'Adding...' : 'Add Customer'}
                                        </Button>
                                        <Button color="secondary" className="ms-2" onClick={handleCancel} type="button">
                                            Cancel
                                        </Button>
                                    </div>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default AddCustomer;
